/**
 * Canonical value-set module for therapy-service-type and episode-of-care types.
 *
 * PRD source of truth:
 *   - docs/prd/EpisodesOfCare/PRD_EPISODES_OF_CARE_FHIR_MAPPING.md
 *   - docs/prd/SessionTypes/PRD_SESSION_TYPES_FHIR_MAPPING.md
 *   - docs/prd/SessionTypes/PRD_EPISODE_OF_CARE_TYPES_FHIR_MAPPING.md
 *
 * Both EpisodeOfCare.type and Appointment/Encounter service types use the SAME
 * CodeSystem: https://unify.health/fhir/CodeSystem/therapy-service-type
 *
 * Episode-of-care type codes are admin-defined via the Services settings catalog;
 * this module provides string-accepting helpers only (no compiled enum).
 */

import type { CodeableConcept, Coding } from '@medplum/fhirtypes';
import { resolveTherapyServiceTypeLabel } from '../episodeTypeDisplayRegistry';

export const THERAPY_SERVICE_TYPE_SYSTEM =
  'https://unify.health/fhir/CodeSystem/therapy-service-type';

/** Look up display label for a therapy-service-type code. */
export function therapyServiceTypeLabel(code: string): string {
  return resolveTherapyServiceTypeLabel(code);
}

/** Build a FHIR Coding from a therapy-service-type code. */
export function therapyServiceTypeCoding(code: string, display?: string): Coding {
  return {
    system: THERAPY_SERVICE_TYPE_SYSTEM,
    code,
    display: display ?? therapyServiceTypeLabel(code),
  };
}

/** Build a FHIR CodeableConcept for EpisodeOfCare.type from a code string. */
export function episodeTypeCodeableConcept(code: string, display?: string): CodeableConcept {
  const coding = therapyServiceTypeCoding(code, display);
  return { coding: [coding], text: coding.display };
}

/**
 * Extract the therapy-service-type code from a CodeableConcept (e.g. EpisodeOfCare.type[0]).
 * Falls back to the first coding code or text if the system doesn't match.
 */
export function codeFromServiceTypeCodeableConcept(
  cc: CodeableConcept | undefined,
): string | undefined {
  if (!cc) return undefined;
  const matched = cc.coding?.find((c) => c.system === THERAPY_SERVICE_TYPE_SYSTEM);
  if (matched?.code) return matched.code;
  return cc.coding?.[0]?.code ?? undefined;
}

// ─────────────────────────────────────────────────────────────────────────────
// Episode-of-care status helpers
// ─────────────────────────────────────────────────────────────────────────────

/** PRD EpisodeOfCare status codes. */
export const EOC_STATUS_OPTIONS = [
  { value: 'planned', label: 'Planned' },
  { value: 'active', label: 'Active' },
  { value: 'onhold', label: 'On Hold (Follow-up)' },
  { value: 'finished', label: 'Finished' },
  { value: 'cancelled', label: 'Cancelled' },
] as const;

/** Status options for EoC creation (only planned/active make sense). */
export function getEocCreationStatusOptions(): { value: string; label: string }[] {
  return [
    { value: 'planned', label: 'Planned' },
    { value: 'active', label: 'Active' },
  ];
}

// ─────────────────────────────────────────────────────────────────────────────
// Backwards compatibility: legacy episode-type system mapping
// ─────────────────────────────────────────────────────────────────────────────

const LEGACY_EPISODE_TYPE_SYSTEM = 'https://unify.health/fhir/CodeSystem/episode-type';

const LEGACY_CODE_MAP: Record<string, string> = {
  therapy: 'individual',
  psychiatry: 'medication-management',
  iop: 'iop',
  supervision: 'individual',
};

/**
 * Display label for legacy or canonical episode type codings.
 * Prefers coding.display when present; falls back to legacy map or code.
 */
export function displayLabelForEpisodeType(cc: CodeableConcept | undefined): string {
  if (!cc) return 'Episode of Care';
  const canonical = cc.coding?.find((c) => c.system === THERAPY_SERVICE_TYPE_SYSTEM);
  if (canonical?.code) {
    const resolved = therapyServiceTypeLabel(canonical.code);
    const display = canonical.display?.trim();
    if (
      display &&
      display.toLowerCase() !== canonical.code.toLowerCase() &&
      display !== resolved
    ) {
      return display;
    }
    return resolved;
  }
  const legacy = cc.coding?.find((c) => c.system === LEGACY_EPISODE_TYPE_SYSTEM);
  if (legacy?.code) {
    const mapped = LEGACY_CODE_MAP[legacy.code];
    if (mapped) return therapyServiceTypeLabel(mapped);
    return legacy.display ?? legacy.code;
  }
  const textNorm = cc.text?.trim().toLowerCase();
  if (textNorm === 'therapy') return therapyServiceTypeLabel('individual');
  const firstDisplay = cc.coding?.[0]?.display?.trim().toLowerCase();
  if (firstDisplay === 'therapy') return therapyServiceTypeLabel('individual');
  const fallbackCode = cc.coding?.[0]?.code;
  if (fallbackCode) return therapyServiceTypeLabel(fallbackCode);
  const text = cc.text?.trim();
  if (text) {
    const code = cc.coding?.[0]?.code;
    if (!code || text.toLowerCase() === code.toLowerCase()) {
      return therapyServiceTypeLabel(code ?? text);
    }
    return text;
  }
  return cc.coding?.[0]?.display ?? 'Episode of Care';
}
